import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { getTableColumnTotal } from "us.collection.economy/components/Remit/Functions";
import Common from "us.common";
import { getRouteUrl } from "us.helper";
import {
  RemitDetailsTableColumn,
  REMIT_DETAILS_TABLE_COLUMNS,
} from "./constants";
import { RootState } from "us.helper/types";

const { $AmountLabel, $Empty, $TableTree } = Common.Components;

/**
 * @description - Remit details table
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2858189310/Remit+Proposal+UI+Design+and+Implementations
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 14/06/2024
 */

const RemitDetailsTable: React.FC<PropsFromRedux> = ({
  remitSummaryByCreditor,
}) => {
  const { t } = useTranslation();
  const { remitDetails } = remitSummaryByCreditor?.data ?? [];

  /**
   * @description - Handle navigation from a link by id or number
   * @param id - id of the column
   * @param linkId - Clicked id to refer the link dashboard
   */
  const handleLinks = (title: string, linkId: number) => {
    switch (title) {
      case "AR_NO":
        getRouteUrl.ar(linkId);
        break;
      case "CASE_NO":
        getRouteUrl.caseType(linkId);
        break;
      case "PAYMENT_ID":
        getRouteUrl.mapPayment(linkId);
        break;
      default:
        break;
    }
  };

  // generate table columns
  const getColumns: any = () => {
    const columns: Array<any> = [];

    REMIT_DETAILS_TABLE_COLUMNS.map(({ key, title }: any) => {
      let column: any = {
        key,
        dataIndex: key,
        title: title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : "",
        customSorter: (a: any, b: any) => a - b,
      };
      if (key === RemitDetailsTableColumn.AR_NO) {
        column = {
          ...column,
          customRenderChild: (text: any, record: any) => {
            return (
              <div>
                <a
                  data-testid={`link-${key}`}
                  onClick={() => handleLinks(title, record[key])}
                >
                  {text}
                </a>
              </div>
            );
          },
        };
      } else if (
        [
          RemitDetailsTableColumn.CASE_NO,
          RemitDetailsTableColumn.PAYMENT_ID,
        ].includes(key)
      ) {
        column = {
          ...column,
          customFilter: true,
          showFilters: true,
          title: title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : "",
          customRenderChild: (text: any, record: any) => {
            return (
              <div>
                <a
                  data-testid={`link-${key}`}
                  onClick={() => handleLinks(title, record[key])}
                >
                  {text}
                </a>
              </div>
            );
          },
        };
      } else if (key == RemitDetailsTableColumn.DEBTOR) {
        column = {
          ...column,
          customRenderChild: (text: any) => {
            return (
                <span>{text}</span>
            );
          },
        };
      } else {
        column = {
          ...column,
          align: "right",
          with: "100px",
          title: () => (
            <div className="mr-2">
              <div>
                {title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : ""}
              </div>
              <div className="header-custom-amount">
                <$AmountLabel value={getTableColumnTotal(key, remitDetails)} />
              </div>
            </div>
          ),
          customRenderChild: (text: any, record: any) => {
            return (
              <span data-testid={`amount-${key}`}>
                <$AmountLabel value={record[key]} />
              </span>
            );
          },
        };
      }
      columns.push(column);
    });

    return columns;
  };

  return (
    <$TableTree
      data-testid="table-remit-details"
      rowKey={(record: any) => record.creditorNo}
      data={remitDetails}
      size="small"
      className="mt-3 header-custom-tag"
      onSort={(sortData, dataSource) => {
        return sortData(dataSource);
      }}
      onFilter={(searchData, dataSource) => {
        return searchData(dataSource);
      }}
      filterOnType
      resetOnSourceChange
      bordered
      showResetAllFilters
      pagination={{
        defaultPageSize: 20,
      }}
      scroll={{
        x: 900,
        y: "calc(100vh - 265px)",
      }}
      columns={getColumns()}
      locale={{
        emptyText: (
          <$Empty
            image={$Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className="text-center">
                <div>{t("US.COLLECTION.COMMON:COMMON.NO_DATA")}</div>
              </div>
            }
          />
        ),
      }}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const { remit } = state;

  const { remitValidation, remitSummaryByCreditor } = remit;
  return {
    remitValidation,
    remitSummaryByCreditor,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RemitDetailsTable);
